/**
 * Where this browser hides its install control, and what to tell somebody.
 *
 * ASK THE BROWSER, never sniff the user agent — `beforeinstallprompt` fires
 * only when this browser can actually install and has not already, so its
 * presence is the only honest test of CAPABILITY. This file answers a
 * different question: once we know the browser will not install itself, the
 * only way to say WHERE the button is, is to know which browser is asking.
 * Apple has no install API at all, so this much has always been necessary;
 * the rest is the same reasoning applied to everyone else.
 *
 * Ported from the members app (mymdv.org), where the first version was too
 * vague for people who needed it most. Keep the two in step.
 */

/** Ordered by how stuck somebody is — see `whereToLook`. */
export type Where = 'ios' | 'iosOther' | 'webview' | 'android' | 'desktop' | 'other'

/**
 * Arguments rather than globals so this can be tested without a browser.
 *
 * The order of the checks matters, because the earlier cases cannot be
 * rescued by the later advice: inside another app's browser there is no menu
 * to open, so "open the menu" is not merely unhelpful, it is wrong.
 *
 * The fallback stays deliberately vague. Naming a button that is not there is
 * worse than saying "look in the menu", so anything unrecognised gets the
 * generic line rather than a confident lie.
 */
export function whereToLook(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  touchPoints: number = typeof navigator === 'undefined' ? 0 : (navigator.maxTouchPoints ?? 0),
): Where {
  if (!ua) return 'other'

  /*
   * An iPad is a Macintosh as far as its user agent is concerned — iPadOS 13
   * stopped saying "iPad" so that sites would serve it the desktop layout. So
   * /ipad/ finds nothing on any current iPad, and without this every one of
   * them would be told to look for an install icon in the address bar, which
   * Safari does not put there. A Mac reporting more than one touch point is
   * the accepted way to tell them apart; a real Mac reports none.
   */
  const apple = /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && touchPoints > 1)

  /*
   * An in-app browser. Services links arrive by email and by push, so this is
   * a likely first context, and none of these can install anything. Detection
   * is imperfect by nature — each is a Safari or Chrome build with a marker
   * bolted on — so it only has to catch the common ones, and a miss falls
   * through to advice that is merely unhelpful rather than wrong.
   */
  if (/FBAN|FBAV|Instagram|Line\/|Twitter|WhatsApp|GSA\/|; wv\)/i.test(ua)) return 'webview'

  /*
   * An iPhone, but not Safari. Chrome and Firefox here are WebKit in a
   * different coat, and the share sheet they show is not the one with "Add to
   * Home Screen" on every iOS version still in use.
   */
  if (apple && /CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return 'iosOther'
  if (apple) return 'ios'

  if (/android/i.test(ua)) return 'android'
  if (/windows|macintosh|linux/i.test(ua) && !/mobile/i.test(ua)) return 'desktop'
  return 'other'
}

/** Running from the home screen already — there is nothing to offer. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/**
 * A step: the instruction, then the detail underneath it.
 *
 * `**like this**` marks the name of a button exactly as it appears on screen.
 * The copy is written here, never typed by anybody, so the component splits on
 * the marker rather than rendering HTML.
 */
export type Step = readonly [instruction: string, detail: string]

/**
 * Written instructions, per context.
 *
 * Deliberately long-winded. These are read by somebody who has already failed
 * to find the button, often on a phone they did not choose, so every step says
 * both what to tap and what it looks like. The iOS list in particular covers
 * two things people get stuck on: newer iPhones hide Share behind the three
 * dots, and "Add to Home Screen" sits below the fold behind **More**.
 */
export const INSTALL_GUIDE: Record<Where, { title: string; steps: readonly Step[] }> = {
  ios: {
    title: 'On iPhone or iPad',
    steps: [
      ['Find the **Share** button', 'a square with an arrow pointing up, in the bar at the bottom'],
      [
        'Cannot see it? Tap the **three dots**',
        'on newer iPhones it is at the top, next to the address. Open it and **Share** is inside.',
      ],
      ['Scroll down and tap **More**', 'the list is long and what we want is further down'],
      ['Choose **Add to Home Screen**', 'the icon is a small square with a plus sign'],
      ['Tap **Add**, at the top right', 'it now sits with your other apps'],
    ],
  },
  iosOther: {
    title: 'Open it in Safari',
    steps: [
      ['On iPhone only Safari installs it properly', 'you are using another browser'],
      ['Copy the address and open it in **Safari**', 'services.mymdv.org'],
      ['There, tap **Share** or the **three dots**', 'depending how new your iPhone is'],
      ['Scroll, tap **More**, then **Add to Home Screen**', 'and confirm with **Add**'],
    ],
  },
  webview: {
    title: 'Open it in your browser first',
    steps: [
      ['You are inside another app', 'you opened the link from email or a message'],
      ['Tap the **three dots** or **Share**', 'at the top or bottom of the screen'],
      ['Choose **Open in Safari** or **Open in Chrome**', 'from there it can be installed'],
    ],
  },
  android: {
    title: 'On Android',
    steps: [
      ['Tap the **three dots**', 'top right, next to the address'],
      [
        'Look for **Install app**',
        'or **Add to Home screen**. If you cannot see it, scroll down the menu.',
      ],
      ['Confirm with **Install**', 'it now sits with your other apps'],
    ],
  },
  desktop: {
    title: 'On a computer',
    steps: [
      ['Look at the end of the address bar', 'there is a small install icon'],
      ['If it is not there, open the browser menu', 'and look for **Install**'],
    ],
  },
  other: {
    title: 'From the browser menu',
    steps: [['Open the menu', 'and look for **Install** or **Add to Home Screen**']],
  },
}

/**
 * Notifications cannot work at all until the app is installed — on iOS the
 * Push API simply is not there in a Safari tab. Asking first would burn the
 * one permission prompt the browser gives you on a request that cannot
 * succeed, and a denied permission is painful to recover from.
 */
export function pushNeedsInstallFirst(where: Where = whereToLook()): boolean {
  return (where === 'ios' || where === 'iosOther') && !isStandalone()
}
